import { z } from "zod";
import { activationLinkStatusSchema } from "../domain";

const nonEmpty = z.string().min(1);

// A trimmed reference shape, not the full Company/Currency models those
// features own — importing them here would cross the module boundary for a
// handful of fields the auth screens never touch.
export const authCurrencyDtoSchema = z.object({
  id: nonEmpty,
  code: z.string().min(1),
  nameEn: nonEmpty,
  nameAr: nonEmpty,
  symbol: nonEmpty,
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export const authAccountDtoSchema = z.object({
  id: nonEmpty,
  type: z.enum(["COMPANY", "INDIVIDUAL"]),
  nameEn: nonEmpty,
  nameAr: nonEmpty,
  email: z.email(),
  phoneNumber1: nonEmpty,
  phoneNumber2: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  currency: authCurrencyDtoSchema.optional(),
});

export const userTypeDtoSchema = z.enum([
  "ADMIN",
  "EMPLOYEE",
  "CLIENT_ADMIN",
  "CLIENT_EMPLOYEE",
]);

export const authUserDtoSchema = z.object({
  id: nonEmpty,
  username: nonEmpty,
  email: z.email(),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
  // null for the tenant's own staff (ADMIN/EMPLOYEE).
  account: authAccountDtoSchema.nullable(),
  type: userTypeDtoSchema,
  permissions: z.array(nonEmpty),
});

export const sessionDtoSchema = z.object({
  accessToken: nonEmpty,
  user: authUserDtoSchema,
});

export const loginResultDtoSchema = z.union([
  z.object({ requiresOtp: z.literal(true) }),
  sessionDtoSchema.extend({ requiresOtp: z.literal(false) }),
]);

export const messageResponseDtoSchema = z.object({ message: nonEmpty });
export const sessionResponseDtoSchema = z.object({ data: sessionDtoSchema });
export const loginResponseDtoSchema = z.object({ data: loginResultDtoSchema });

export const passwordResetOtpResponseDtoSchema = z.object({
  data: z.object({
    resetToken: nonEmpty,
    expiresAt: nonEmpty,
  }),
  message: nonEmpty,
});
export const activationLinkResponseDtoSchema = z.object({
  data: z.object({
    status: activationLinkStatusSchema,
    email: z.string().nullable(),
    expiresAt: z.string().nullable(),
  }),
  message: nonEmpty,
});

export type UserTypeDto = z.infer<typeof userTypeDtoSchema>;
export type AuthUserDto = z.infer<typeof authUserDtoSchema>;
export type SessionDto = z.infer<typeof sessionDtoSchema>;
export type LoginResultDto = z.infer<typeof loginResultDtoSchema>;
